import { LitElement, css } from "lit-element";
import { classMap } from "lit-html/directives/class-map";
import { html } from "lit-html";
import { storeInstance } from "store/index.js";
import { Router } from "@vaadin/router/dist/vaadin-router.js";
import KbParser from "../utils/KbParser";
import "@vaadin/vaadin-icons/vaadin-icons.js";
import "bds-tree";
import "./bds-property-filter";

/**
 * @property {Object} item currently selected category
 * @property {Array} searchResults results from publication search
 * @property {Array} categories  category tree
 * @property {Array} controlDefs describes the type, id and other metadata about control
 * @property {Array} controlData contains user visible data and other values
 * @property {Object} propertyFilterState used to set the initial state for the property filter
 */

class KbPubsearch extends LitElement {
  constructor() {
    super();
  }

  static get properties() {
    return {
      item: { type: Object },
      searchResults: { type: Array },
      categories: { type: Array },
      controlDefs: { type: Array },
      controlData: { type: Array },
      propertyFilterState: { type: Array },
      resultsClasses: { type: Object },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.subscription = storeInstance.subscribe((state) => {
      this.stateChange(state);
    });
    this.state = storeInstance.state;
    this.string = this.state.strings;
    this.item = this.state.item;
    this.searchResults = this.state.pub_search_results;
    this.categories = this.state.categories;
    this.controlDefs = this.state.panel_control_defs;
    this.controlData = this.state.control_def_data_array;
    this.resultsClasses = {
      results: this.controlDefs.length > 0,
      "results--empty": this.controlDefs.length == 0,
    };
    // this.getPanelControls()
    this.parseQueryString();
    this.getEmptySearch()
  }

  stateChange(state) {
    this.state = state;
    this.string = this.state.strings;
    this.item = this.state.item;
    this.searchResults = this.state.pub_search_results;
    this.categories = this.state.categories;
    this.controlDefs = this.state.panel_control_defs;
    this.controlData = this.state.control_def_data_array;
    this.resultsClasses = {
      results: this.controlDefs.length > 0,
      "results--empty": this.controlDefs.length == 0,
    };
    // this.getPanelControls()
    this.parseQueryString();
  }

  getPanelControls() {
    if (Array.isArray(this.controlDefs) && this.controlDefs.length === 0) {
      storeInstance.dispatch("getPanelControls")
    }
  }

  getEmptySearch() {
    if (window.location.search === "") {
      storeInstance.dispatch("getPanelPubSearchResults", {
        PanelControlValues: { lstValues: [] },
      });
    }
  }

  getIdFromControlDef(key) {
    let foundControlDef = this.controlDefs.find(
      (controlDef) => controlDef.id == key
    );
    return foundControlDef?.id;
  }

  parseQueryString() {
    this.propertyFilterState = {};
    let queryParams = new URLSearchParams(window.location.search);
    for (const [key, value] of queryParams) {
      let controlDefId = this.getIdFromControlDef(key);
      if (controlDefId) {
        this.propertyFilterState[controlDefId] = value;
      }
    }
  }

  lstValuesToString(lstValues) {
    return lstValues.map((item) => item.id).join(",");
  }

  generateQueryString(controlValues) {
    let searchParams = new URLSearchParams();
    Object.keys(controlValues).forEach((controlDefId) => {
      searchParams.set(
        `${controlDefId}`,
        this.lstValuesToString(controlValues[controlDefId])
      );
    });
    if (this.searchTerm) {
      searchParams.set("searchTerm", this.searchTerm);
    }
    return searchParams.toString();
  }

  handlePropertyFilterChange(e) {
    if (e.detail?.lstValues) {
      let queryString = this.generateQueryString(e.detail.lstValues);
      this.updateUrl(queryString);
      if(window.location.href.includes("pubsearch")) {
        storeInstance.dispatch("getPanelPubSearchResults", {
          PanelControlValues: { lstValues: this.controlValuesToLstValues(e.detail.lstValues) },
        });
      }
    }
  }

    // convert controlValues to lstValues format
    controlValuesToLstValues(controlValues) {
      let keys = Object.keys(controlValues).filter(
        (key) => controlValues[key].length > 0
      );
      return keys.map((controlId) => ({
        controlDefId: controlId,
        lstValues: controlValues[controlId],
      }));
    }

  
  filterStateToControlValues(filterState) {
    let controlValues = {};
    let controlDefIds = Object.keys(filterState);
    if (Array.isArray(controlDefIds) && controlDefIds.length > 0) {
      controlDefIds.forEach((controlDefID) => {
        controlValues[controlDefID] = filterState[controlDefID]
          .split(",")
          .map((value) => ({ id: value, value: value }));
      });
      return controlValues;
    }
  }

  // Changes current url to match active filters
  updateUrl(queryString) {
    if (queryString) {
      if (`?${queryString}` !== window.location.search) {
        let newUrl = `/kb/${this.state.langCode}/pubsearch?${queryString}`;
        history.pushState({}, null, newUrl);
      }
    }
  }

  async handleLinkClick(publication) {
    if (publication.kbResId) {
      await this._gotoItem(publication.kbResId);
    } else {
      Router.go("/kb/" + this.state.langCode + "/" + publication.itemExtId);
    }
  }

  async _gotoItem(kbResId) {
    this.parser = new KbParser();
    this.parser.setHref(kbResId);
    let newLink = await this.parser.parse();
    Router.go(newLink);
  }

  iconTemplate(iconName, type) {
    return html`<span class=${`badge badge-${type}`}
      >${type} <iron-icon icon=${`${iconName}`}></iron-icon
    ></span>`;
  }

  publicationIconTemplate(type) {
    switch (type) {
      case "File":
        return this.iconTemplate("vaadin:file-text", type);
      case "Download":
        return this.iconTemplate("vaadin:file-text", type);
      case "Helpset":
        return this.iconTemplate("vaadin:file-tree-sub", type);
    }
  }

  render() {
    return html` <div>
      <div class="main">
        <bds-property-filter
          class="filter"
          @filterChange=${this.handlePropertyFilterChange}
          .controlDefs=${this.controlDefs}
          .controlData=${this.controlData}
          .initialState=${this.propertyFilterState}
        >
        </bds-property-filter>
        <section class=${classMap(this.resultsClasses)}>
          <h1 style="font-size: 1.25rem; margin: 0 0 0 0.25rem;">
            Browse Publications
          </h1>
          <ul style="margin: 0; padding: 0">
            ${this.searchResults.length < 1
              ? html`<p>No publications found</p>`
              : null}
            ${this.searchResults.map(
              (publication) =>
                html`<li
                  class="result"
                  @click=${async () => await this.handleLinkClick(publication)}
                >
                  <a class="result-content" data-href=${publication.kbResId}>
                    <div>
                      <h3 class="result__name">${publication.name}</h3>
                      <p class="result__description">${publication.description}</p>
                    </div>
                    <div class="result__details-container">
                      ${this.publicationIconTemplate(publication.itemType)}
                    </div>
                    <!-- <span>${publication.lastModDate}</span> -->
                  </a>
                </li>`
            )}
          </ul>
        </section>
      </div>
    </div>`;
  }

  static get styles() {
    return css`
      .result {
        list-style: none;
        padding: 0.5rem;
        border-bottom: 2px solid var(--kb-publication-result-border-color);
        margin: 1rem 0px;
      }

      .result-content {
        display: flex;
        justify-content: space-between;
        flex-direction: column;
      }

      .badge {
        padding: 0.25rem 0.75rem;
        border-radius: 20px;
        font-size: 0.9375rem;
        display: flex;
        align-items: center;
        gap: 4px;
        height: fit-content;
        align-self: end;
      }

      .result__name {
        color: var(--kb-publication-result-name-color);
      }

      .result__description {
        color: var(--kb-publication-result-description-color)
      }

      iron-icon {
        width: 1rem;
        position: inherit;
      }

      .results {
        display: block;
        margin: 2rem 1rem;
      }

      .result__details-container {
          display: flex;
          justify-content: flex-end;
        }

      .last-modified {
        font-size: 0.875rem;
      }

      .result:hover {
        cursor: pointer;
        background-color: var(--kb-publication-result-highlight);
      }

      .filter {
        background-color: var(--kb-property-filter-background);
      }

      @media only screen and (min-width: 640px) {
        .main {
          display: grid;
          grid-template-columns: repeat(12, 1fr);
        }

        .result-content {
          flex-direction: row;
        }

        .filter {
          display: block;
          grid-column: 1 / span 3;
        }
        .results {
          grid-column: 4 / span 8;
          padding: 2.5rem 0rem;
          margin-top: 0;
        }
        .results--empty {
          grid-column: 1 / span 8;
          margin-left: 2.5rem;
        }
      }

      @media only screen and (min-width: 1400px) {
        .filter {
          grid-column: 1 / span 2;
        }

        .results {
          grid-column: 3 / span 9;
          padding: 2rem 0rem;
        }

        .results--empty {
          grid-column: 1 / span 8;
        }
      }
    `;
  }
}

customElements.define("kb-pubsearch", KbPubsearch);

{
  /* <bds-tree
.initialNodeId=${this.item.item.externalId}
style="overflow-wrap: anywhere;"
@treeChange=${this.handleTreeChange}
.nodes=${this.categories?.children}
labelPath="item.externalId"
idPath="item.externalId"
dataPath="item"
></bds-tree> */
}
